/**
 * ConsentModalComponent — Blocking modal for Habeas Data policy acceptance.
 *
 * Shown on first login (or after policy update) when the technician has not
 * accepted the current version of the data treatment policy.
 * Cannot be dismissed without accepting.
 *
 * @see Ley 1581 de 2012, Decreto 1377 de 2013
 * @see .kiro/specs/habeas-data-compliance/requirements.md Req 4
 */
import {
  Component,
  ChangeDetectionStrategy,
  inject,
  signal,
  Input,
} from "@angular/core";
import { ModalController } from "@ionic/angular/standalone";
import {
  IonContent,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButton,
  IonSpinner,
} from "@ionic/angular/standalone";
import { POLICY_URL, CURRENT_POLICY_VERSION } from "@fsm/shared";
import { ApiService } from "../../../core/services/api.service";
import { PersistentStorageService } from "../../../core/services/persistent-storage.service";
import { firstValueFrom } from "rxjs";

@Component({
  selector: "fsm-consent-modal",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IonContent, IonHeader, IonToolbar, IonTitle, IonButton, IonSpinner],
  template: `
    <ion-header>
      <ion-toolbar color="primary">
        <ion-title>Tratamiento de Datos</ion-title>
      </ion-toolbar>
    </ion-header>

    <ion-content class="ion-padding">
      <div class="consent-content">
        <div class="icon-container">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.5"
            stroke="currentColor"
            class="consent-icon"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z"
            />
          </svg>
        </div>

        <h2 class="consent-title">
          Autorización para el Tratamiento de Datos Personales
        </h2>

        <p class="consent-text">
          Para usar la aplicación, necesitamos tu autorización para recolectar y
          tratar tus datos personales (nombre, teléfono, ubicación durante
          servicios) con el fin de asignar y gestionar órdenes de trabajo,
          enviarte notificaciones operativas y generar reportes de servicio.
        </p>

        <p class="consent-text">
          Puedes conocer, actualizar, rectificar o solicitar la supresión de tus
          datos en cualquier momento escribiendo a
          <strong>datos&#64;sifagent.co</strong>.
        </p>

        <a
          [href]="policyUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="policy-link"
        >
          Ver Política completa de Tratamiento de Datos →
        </a>

        @if (errorMessage()) {
          <p class="error-text">{{ errorMessage() }}</p>
        }

        @if (readOnly) {
          <ion-button
            expand="block"
            color="primary"
            class="accept-button"
            (click)="close()"
          >
            Cerrar
          </ion-button>
        } @else {
          <ion-button
            expand="block"
            color="primary"
            class="accept-button"
            [disabled]="loading()"
            (click)="acceptPolicy()"
          >
            @if (loading()) {
              <ion-spinner name="crescent" slot="start" />
              Registrando...
            } @else {
              Acepto la Política de Datos
            }
          </ion-button>
        }
      </div>
    </ion-content>
  `,
  styles: [
    `
      .consent-content {
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 1rem 0;
      }
      .icon-container {
        margin-bottom: 1rem;
      }
      .consent-icon {
        width: 3.5rem;
        height: 3.5rem;
        color: var(--ion-color-primary);
      }
      .consent-title {
        font-size: 1.25rem;
        font-weight: 700;
        text-align: center;
        margin-bottom: 1rem;
        color: var(--ion-text-color);
      }
      .consent-text {
        font-size: 0.9rem;
        line-height: 1.5;
        color: var(--ion-color-medium);
        margin-bottom: 0.75rem;
        text-align: center;
      }
      .policy-link {
        display: inline-block;
        margin: 1rem 0 1.5rem;
        font-size: 0.875rem;
        font-weight: 500;
        color: var(--ion-color-primary);
        text-decoration: underline;
      }
      .accept-button {
        width: 100%;
        margin-top: 1rem;
      }
      .error-text {
        color: var(--ion-color-danger);
        font-size: 0.8rem;
        text-align: center;
        margin-top: 0.5rem;
      }
    `,
  ],
})
export class ConsentModalComponent {
  private readonly modalCtrl = inject(ModalController);
  private readonly api = inject(ApiService);
  private readonly persistent = inject(PersistentStorageService);

  /**
   * Read-only mode: opened from the Profile screen to review the authorization
   * at any time (not the blocking first-login flow). In this mode the button
   * just closes the modal and no consent is registered.
   */
  @Input() readOnly = false;

  readonly policyUrl = POLICY_URL;
  readonly loading = signal(false);
  readonly errorMessage = signal("");

  /** Dismiss without registering consent (read-only review mode). */
  async close(): Promise<void> {
    await this.modalCtrl.dismiss({ accepted: false });
  }

  async acceptPolicy(): Promise<void> {
    this.loading.set(true);
    this.errorMessage.set("");

    try {
      await firstValueFrom(
        this.api.patch("/v1/team/me/consent", {
          policyVersion: CURRENT_POLICY_VERSION,
        }),
      );

      // Store durably (Preferences on native — survives storage pressure) to
      // avoid re-checking on next app open (Req 15.2).
      await this.persistent.set("fsm_consent_version", CURRENT_POLICY_VERSION);

      await this.modalCtrl.dismiss({ accepted: true });
    } catch {
      this.errorMessage.set(
        "Error al registrar. Verifica tu conexión e intenta de nuevo.",
      );
    } finally {
      this.loading.set(false);
    }
  }
}
